import { useEffect, useState } from 'react';
import { useBoomWallet } from '@boomeme/wallet-sdk';
import userApiService from '@/request/user/user.api.service';
import { ITransfer, ITransferTokenMetadata } from '@/request/user/user.response.interface';

export interface ITransferData extends ITransfer {
  token_address: string;
  token_icon: string;
  token_name: string;
  token_symbol: string;
}

export default () => {
  const { walletAddress, chain } = useBoomWallet();
  const [transferMap, setTransferMap] = useState<{ [key: string]: ITransferData[] }>({});
  const [loading, setLoading] = useState(false);
  const getTransferList = async () => {
    try {
      if (!walletAddress || chain !== 'solana') return;
      setLoading(true);
      const res = await userApiService.getAddressTransfer({
        address: walletAddress as string,
        tokenAddress: 'So11111111111111111111111111111111111111111',
      });
      if (res.code !== 0 || !res.data.items.length) return;
      const { items } = res.data;
      const { tokens } = res.data.metadata;

      const normalizedTokens = normalizeTokens(tokens);

      const singleTransfers = filterSingleTransfers(items);

      const dateGroupedTransfers = groupAndSortByDate(singleTransfers);

      const enrichedTransfers = enrichTransfersWithTokenInfo(dateGroupedTransfers, normalizedTokens);

      setTransferMap(enrichedTransfers);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const normalizeTokens = (tokens: ITransferTokenMetadata): { [key: string]: ITransferTokenMetadata } => {
    return Object.entries(tokens).reduce((acc: { [key: string]: any }, [key, value]) => {
      acc[key.toLowerCase()] = value;
      return acc;
    }, {} as ITransferTokenMetadata);
  };

  const filterSingleTransfers = (transfers: ITransfer[]): ITransfer[] => {
    const grouped = transfers.reduce((acc, item) => {
      acc[item.trans_id] = [...(acc[item.trans_id] || []), item];
      return acc;
    }, {} as { [key: string]: ITransfer[] });

    return Object.values(grouped)
      .filter((group) => group.length === 1)
      .map((group) => group[0])
      .sort((a, b) => a.block_time - b.block_time);
  };

  const groupAndSortByDate = (transfers: ITransfer[]): { [key: string]: ITransfer[] } => {
    const groupedByDate = transfers.reduce((acc, transfer) => {
      const date = transfer.time.split('T')[0];
      acc[date] = [...(acc[date] || []), transfer];
      return acc;
    }, {} as { [key: string]: ITransfer[] });

    return Object.entries(groupedByDate)
      .sort(([dateA], [dateB]) => new Date(dateB).getTime() - new Date(dateA).getTime())
      .reduce((acc, [date, transfers]) => {
        acc[date] = transfers;
        return acc;
      }, {} as { [key: string]: ITransfer[] });
  };

  const enrichTransfersWithTokenInfo = (
    groupedTransfers: { [key: string]: ITransfer[] },
    tokens: { [key: string]: ITransferTokenMetadata },
  ): { [key: string]: ITransferData[] } => {
    return Object.entries(groupedTransfers).reduce((acc: { [key: string]: any }, [date, transfers]) => {
      acc[date] = transfers
        .map((transfer) => {
          const tokenAddress = transfer.token_address.toLowerCase();
          return tokens[tokenAddress] ? { ...transfer, ...tokens[tokenAddress] } : null;
        })
        .filter((transfer): transfer is ITransferData => transfer !== null);
      return acc;
    }, {} as ITransferData);
  };

  useEffect(() => {
    getTransferList();
  }, [walletAddress, chain]);

  return {
    transferMap,
    loading,
  };
};
